import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { tokenManager, userManager } from '../lib/auth';
import { handleAuthFailure } from '../lib/authErrorHandler';
import { authAPI } from '../services/api/authApi';
import {
  AuthState,
  AuthContextType,
  AuthAction,
  AuthResponse,
  User,
  UserRole,
  Permission,
  RegisterData,
  RegisterResponse,
} from '../schemas/auth';
import { clearCachedQuestions } from '../utils/recommendedQuestionsCache';
import { getErrorKey } from '../lib/errors';

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  permissions: [],
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOAD_USER_START: 'LOAD_USER_START',
  LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
  LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
} as const;

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
    case AUTH_ACTIONS.LOAD_USER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
    case AUTH_ACTIONS.LOAD_USER_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        permissions: action.payload.permissions || [],
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
    case AUTH_ACTIONS.LOAD_USER_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        permissions: [],
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        permissions: [],
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload } as User,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from storage on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = tokenManager.getAccessToken();
      const storedUser = userManager.getUser();

      if (token && storedUser) {
        try {
          // Verify token is still valid
          if (tokenManager.isTokenExpired(token)) {
            // Use centralized auth error handler for consistency
            handleAuthFailure('Token expired on app load');
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
            return;
          }

          // Optionally refresh user data from server
          try {
            const response = await authAPI.getCurrentUser();
            const freshUserData = response.data;
            userManager.setUser(freshUserData);

            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
              payload: {
                user: freshUserData,
                permissions: getPermissionsForRole(freshUserData.role),
              },
            });
            return;
          } catch (apiError) {
            // If we can't refresh user data, continue with stored user data
            console.warn('Could not refresh user data, using stored data:', apiError);
          }

          dispatch({
            type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
            payload: {
              user: storedUser,
              permissions: getPermissionsForRole(storedUser.role),
            },
          });
        } catch (error) {
          tokenManager.clearTokens();
          userManager.clearUser();
          dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: (error as Error).message });
        }
      } else {
        // No valid session - this is normal for users who haven't logged in yet
        // Don't show an error, just set the state to not authenticated
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    loadUser();
  }, []);

  // Login function
  const login = useCallback(
    async (email: string, password: string): Promise<AuthResponse> => {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      try {
        // Call actual API
        const tokenResponse = await authAPI.login({ email, password });
        const { access_token, refresh_token } = tokenResponse.data;

        // Store tokens
        tokenManager.setTokens(access_token, refresh_token);

        // Get user data with the new token
        const userResponse = await authAPI.getCurrentUser();
        const user = userResponse.data;

        // Store user data
        userManager.setUser(user);

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user,
            permissions: (user.permissions ||
              getPermissionsForRole(user.role as UserRole)) as Permission[],
          },
        });

        return { success: true };
      } catch (error) {
        const errorKey = getErrorKey(error, 'login');
        const errorMessage = t(errorKey);
        dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [t]
  );

  // Register function
  const register = useCallback(
    async (userData: RegisterData): Promise<AuthResponse> => {
      dispatch({ type: AUTH_ACTIONS.REGISTER_START });

      try {
        // Call actual API - register endpoint now returns tokens along with user data
        const response = await authAPI.register(userData);
        const registerData: RegisterResponse = response.data;

        const { access_token, refresh_token } = registerData;

        // Store tokens
        tokenManager.setTokens(access_token, refresh_token);

        // Get full user data with the new token
        const userResponse = await authAPI.getCurrentUser();
        const user = userResponse.data;

        // Store user data
        userManager.setUser(user);

        dispatch({
          type: AUTH_ACTIONS.REGISTER_SUCCESS,
          payload: {
            user,
            permissions: (user.permissions ||
              getPermissionsForRole(user.role as UserRole)) as Permission[],
          },
        });

        return { success: true };
      } catch (error: any) {
        // Handle validation errors from the API
        let errorMessage = t('errors.unknown');

        // Check for error message first (for common errors like "Email already registered")
        if (error?.response?.data?.error?.message) {
          const apiMessage = error.response.data.error.message;
          // Map common API error messages to user-friendly translations
          if (apiMessage === 'Email already registered') {
            errorMessage = t('auth.errors.emailAlreadyRegistered');
          } else if (apiMessage.includes('User with this email') && apiMessage.includes('exists')) {
            errorMessage = t('auth.errors.emailAlreadyRegistered');
          } else {
            errorMessage = apiMessage;
          }
        } else if (error?.response?.data?.error?.details) {
          // Handle Pydantic validation errors
          const validationErrors = error.response.data.error.details;
          if (Array.isArray(validationErrors)) {
            // Get the first validation error message
            const firstError = validationErrors[0];
            if (firstError?.msg) {
              errorMessage = firstError.msg;
            }
          }
        } else if (error?.response?.data?.message) {
          // Handle errors where message is at the top level of response.data
          const apiMessage = error.response.data.message;
          if (apiMessage === 'Email already registered') {
            errorMessage = t('auth.errors.emailAlreadyRegistered');
          } else {
            errorMessage = apiMessage;
          }
        } else {
          // Use centralized error handling for network/auth errors
          const errorKey = getErrorKey(error, 'login'); // Use 'login' context for register too
          errorMessage = t(errorKey);
        }

        dispatch({ type: AUTH_ACTIONS.REGISTER_FAILURE, payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    },
    [t]
  );

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call API to logout (invalidate refresh token)
      await authAPI.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local data even if API call fails
      tokenManager.clearTokens();
      userManager.clearUser();
      clearCachedQuestions(); // Clear recommended questions cache
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, []);

  // Update user function
  const updateUser = useCallback(
    (userData: Partial<User>) => {
      const updatedUser = { ...state.user, ...userData } as User;
      userManager.setUser(updatedUser);
      dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData });
    },
    [state.user]
  );

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Check permissions
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!state.user) return false;
      return state.permissions.includes(permission);
    },
    [state.permissions, state.user]
  );

  const hasAnyPermission = useCallback(
    (permissionsArray: Permission[]): boolean => {
      if (!state.user) return false;
      return permissionsArray.some(permission => state.permissions.includes(permission));
    },
    [state.permissions, state.user]
  );

  const hasAllPermissions = useCallback(
    (permissionsArray: Permission[]): boolean => {
      if (!state.user) return false;
      return permissionsArray.every(permission => state.permissions.includes(permission));
    },
    [state.permissions, state.user]
  );

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    clearError,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to get permissions for role
const getPermissionsForRole = (role: UserRole): Permission[] => {
  const roles: Record<UserRole, Permission[]> = {
    admin: [
      'read',
      'write',
      'delete',
      'admin',
      'manage_users',
      'manage_models',
      'manage_knowledge',
      'manage_literature',
    ],
    user: ['read', 'write'],
    viewer: ['read'],
  };
  return roles[role] || [];
};

export default AuthContext;
